import React, { createContext, useContext, useState, useCallback, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';
import SocketManagerSimple from '../lib/socket/SocketManagerSimple';
import MediasoupClient from '../lib/webrtc/MediasoupClient';
import { useRoomStore } from '../stores/roomStore';
import { useParticipantsStore } from '../stores/participantsStore';
import { useMediaStore } from '../stores/mediaStore';
import { useChatStore } from '../stores/chatStore';
import { useConnectionStore } from '../stores/connectionStore';
import { getUserMedia } from '../hooks/useMediaDevices';
import { AudioLevelDetector } from '../lib/audioLevelDetector';
import { applyBackgroundBlur } from '../lib/BackgroundBlurProcessor';

const getOrCreateUserId = (): string => {
  const stored = localStorage.getItem('userId');
  if (stored) return stored;

  const newId = typeof crypto !== 'undefined' && 'randomUUID' in crypto
    ? crypto.randomUUID()
    : `uid-${Date.now()}-${Math.random().toString(16).slice(2)}`;

  localStorage.setItem('userId', newId);
  return newId;
};

// Данные для переподключения
interface RejoinData {
  roomId: string;
  userId: string;
  displayName: string;
  password?: string;
  audioEnabled: boolean;
  videoEnabled: boolean;
}

type RecordingMode = 'video-audio' | 'audio-only';
type RecordingQuality = 'high' | 'medium' | 'low';

interface RecordingState {
  isRecording: boolean;
  mode: RecordingMode;
  quality: RecordingQuality;
  startedAt: number | null;
}

interface RoomContextType {
  socket: SocketManagerSimple | null;
  mediasoup: MediasoupClient | null;
  joinRoom: (roomId: string, displayName: string, password?: string, overrideAudio?: boolean, overrideVideo?: boolean, createRoom?: boolean) => Promise<void>;
  leaveRoom: () => void;
  toggleAudio: () => void;
  toggleVideo: () => void;
  startScreenShare: () => Promise<void>;
  stopScreenShare: () => void;
  muteParticipant: (participantId: string) => void;
  muteAllParticipants: () => void;
  makeAdmin: (participantId: string) => void;
  kickUser: (participantId: string) => void;
  lowerParticipantHand: (participantId: string) => void;
  endMeeting: () => void;
  toggleRaisedHand: () => void;
  resyncProducers: () => Promise<void>;
  bitrateLevel: 'standard' | 'low';
  setBitrateLevel: (level: 'standard' | 'low') => void;
  recordingState: RecordingState;
  startLocalRecording: (mode: RecordingMode, quality: RecordingQuality) => Promise<void>;
  stopLocalRecording: () => void;
}

const RoomContext = createContext<RoomContextType | null>(null);

export const useRoom = () => {
  const context = useContext(RoomContext);
  if (!context) {
    throw new Error('useRoom must be used within RoomProvider');
  }
  return context;
};

interface RoomProviderProps {
  children: React.ReactNode;
}

export const RoomProvider: React.FC<RoomProviderProps> = ({ children }) => {
  const navigate = useNavigate();
  const [socket, setSocket] = useState<SocketManagerSimple | null>(null);
  const [mediasoup, setMediasoup] = useState<MediasoupClient | null>(null);
  
  // Данные для переподключения
  const rejoinDataRef = useRef<RejoinData | null>(null);
  const isRejoiningRef = useRef(false);
  const mediasoupRef = useRef<MediasoupClient | null>(null);
  const socketRef = useRef<SocketManagerSimple | null>(null);
  const localStreamRef = useRef<MediaStream | null>(null);
  const screenStreamRef = useRef<MediaStream | null>(null);
  const peerStreamsRef = useRef<Map<string, MediaStream>>(new Map());
  const audioDetectorsRef = useRef<Map<string, AudioLevelDetector>>(new Map());
  const isRecoveringTransportRef = useRef(false);
  const resyncProducersRef = useRef<() => Promise<void>>(async () => {});
  const audioEnabledRef = useRef(false);
  const videoEnabledRef = useRef(false);
  const [bitrateLevel, setBitrateLevelState] = useState<'standard' | 'low'>('low');
  const [recordingState, setRecordingState] = useState<RecordingState>({
    isRecording: false,
    mode: 'video-audio',
    quality: 'medium',
    startedAt: null,
  });
  const recordingMediaRecorderRef = useRef<MediaRecorder | null>(null);
  const recordingChunksRef = useRef<Blob[]>([]);
  const recordingStreamRef = useRef<MediaStream | null>(null);
  const recordingScreenStreamRef = useRef<MediaStream | null>(null);
  const recordingAudioContextRef = useRef<AudioContext | null>(null);
  const recordingStopResolverRef = useRef<(() => void) | null>(null);

  const { setJoinedRoom, setInLobby, setRoomId, setPassword: setStoredPassword } = useRoomStore();
  const { setStatus: setConnectionStatus, setReconnectAttempt, setDisconnectReason } = useConnectionStore();
  const { setLocalParticipant, updateLocalParticipant, addParticipant, removeParticipant, updateParticipant, setParticipantStream, setParticipantScreenStream } = useParticipantsStore();
  const { addMessage: addChatMessage, removeMessage: removeChatMessage } = useChatStore();
  const { 
    localStream, 
    setLocalStream, 
    audioEnabled, 
    videoEnabled,
    screenShareEnabled,
    setAudioEnabled,
    setVideoEnabled,
    setScreenShareEnabled,
    audioDeviceId,
    videoDeviceId,
    blurBackground,
  } = useMediaStore();

  const syncLocalMediaState = useCallback((nextAudioEnabled: boolean, nextVideoEnabled: boolean) => {
    audioEnabledRef.current = nextAudioEnabled;
    videoEnabledRef.current = nextVideoEnabled;
    setAudioEnabled(nextAudioEnabled);
    setVideoEnabled(nextVideoEnabled);
    updateLocalParticipant({ audioEnabled: nextAudioEnabled, videoEnabled: nextVideoEnabled });

    if (rejoinDataRef.current) {
      rejoinDataRef.current.audioEnabled = nextAudioEnabled;
      rejoinDataRef.current.videoEnabled = nextVideoEnabled;
    }

    const currentStream = localStreamRef.current;
    const audioTrack = currentStream?.getAudioTracks()[0];
    const videoTrack = currentStream?.getVideoTracks()[0];

    if (audioTrack) {
      audioTrack.enabled = nextAudioEnabled;
    }

    if (videoTrack) {
      videoTrack.enabled = nextVideoEnabled;
    }
  }, [setAudioEnabled, setVideoEnabled, updateLocalParticipant]);

  useEffect(() => {
    audioEnabledRef.current = audioEnabled;
    videoEnabledRef.current = videoEnabled;
  }, [audioEnabled, videoEnabled]);

  const consumeRemoteProducer = useCallback(
    async (
      mediasoupClient: MediasoupClient,
      socketManager: SocketManagerSimple,
      peerId: string,
      producerId: string,
      kind: string,
      appData?: any
    ) => {
      try {
        console.log(`[Room] Consuming producer ${producerId} (${kind}) from peer ${peerId}`, { appData });
        const consumer = await mediasoupClient.consume(producerId);

        console.log('[Room] Consumer created:', {
          consumerId: consumer.id,
          producerId: consumer.producerId,
          kind: consumer.kind,
          track: {
            kind: consumer.track.kind,
            label: consumer.track.label,
            readyState: consumer.track.readyState
          }
        });

        await socketManager.emit('resumeConsumer', { consumerId: consumer.id });
        console.log('[Room] Consumer resumed:', consumer.id);

        const isScreenShare = appData?.screenShare === true;
        if (isScreenShare) {
          const screenStream = new MediaStream([consumer.track]);
          setParticipantScreenStream(peerId, screenStream);
          updateParticipant(peerId, { screenShareEnabled: true });
          console.log(`[Room] Created screenStream for peer ${peerId}`, consumer.track.label);
          return;
        }

        const peerStreams = peerStreamsRef.current;
        let peerStream = peerStreams.get(peerId);
        
        // Always create a new stream to ensure React detects the change
        const newStream = new MediaStream();
        
        // Copy existing tracks of different kind
        if (peerStream) {
          peerStream.getTracks().forEach((track) => {
            if (track.kind !== consumer.track.kind && track.readyState === 'live') {
              newStream.addTrack(track);
            }
          });
        }
        
        // Add the new track
        newStream.addTrack(consumer.track);
        peerStreams.set(peerId, newStream);
        
        console.log(`[Room] Added ${consumer.track.kind} track to peer ${peerId} stream, label: ${consumer.track.label}, readyState: ${consumer.track.readyState}`);
        setParticipantStream(peerId, newStream);
        console.log(`[Room] Updated stream for peer ${peerId}, total tracks:`, newStream.getTracks().map(t => `${t.kind}:${t.label}:${t.readyState}`));

        // Подключаем анализатор звука для аудио треков
        if (consumer.track.kind === 'audio') {
          const existingDetector = audioDetectorsRef.current.get(peerId);
          if (existingDetector) {
            existingDetector.destroy();
          }
          const detector = new AudioLevelDetector(newStream, (isSpeaking) => {
            if (isSpeaking) {
              // Track when speaking started for prioritization
              const participant = useParticipantsStore.getState().participants.get(peerId);
              if (!participant?.speakingStartTime) {
                updateParticipant(peerId, { isSpeaking, speakingStartTime: Date.now() });
              } else {
                updateParticipant(peerId, { isSpeaking });
              }
            } else {
              // Clear speaking start time when stopped
              updateParticipant(peerId, { isSpeaking, speakingStartTime: undefined });
            }
          });
          audioDetectorsRef.current.set(peerId, detector);
        }
      } catch (error) {
        console.error(`[Room] Failed to consume producer ${producerId}:`, error);
      }
    },
    [setParticipantStream, setParticipantScreenStream, updateParticipant]
  );

  // Функция для полной очистки соединения
  const cleanupConnection = useCallback(() => {
    console.log('[Room] Cleaning up connection...');
    
    // Очищаем audio detectors
    audioDetectorsRef.current.forEach(detector => detector.destroy());
    audioDetectorsRef.current.clear();
    
    // Очищаем данные для переподключения - это предотвратит дальнейшие попытки
    rejoinDataRef.current = null;
    isRejoiningRef.current = false;
    
    // Останавливаем все треки
    if (localStream) {
      localStream.getTracks().forEach((track) => track.stop());
      setLocalStream(null);
    }

    // Закрываем mediasoup
    if (mediasoup) {
      mediasoup.close();
      setMediasoup(null);
    }

    // Принудительно останавливаем socket и все reconnect попытки
    if (socket) {
      socket.forceStop();
      setSocket(null);
    }
    
    // Сбрасываем статус соединения
    setConnectionStatus('disconnected');
    
    // Закрываем все toast уведомления о переподключении
    toast.dismiss('reconnect');
    toast.dismiss('rejoin');
  }, [localStream, mediasoup, socket, setLocalStream, setConnectionStatus]);

  const finalizeLocalRecording = useCallback(async () => {
    const recorder = recordingMediaRecorderRef.current;

    if (!recorder || recorder.state === 'inactive') {
      return;
    }

    await new Promise<void>((resolve) => {
      recordingStopResolverRef.current = resolve;
      recorder.stop();
    });
  }, []);

  const stopLocalRecording = useCallback(() => {
    const recorder = recordingMediaRecorderRef.current;

    if (recorder && recorder.state !== 'inactive') {
      recorder.stop();
      return;
    }

    recordingStreamRef.current?.getTracks().forEach((track) => track.stop());
    recordingScreenStreamRef.current?.getTracks().forEach((track) => track.stop());
    recordingAudioContextRef.current?.close().catch(() => {});
    recordingChunksRef.current = [];
    recordingMediaRecorderRef.current = null;
    recordingStreamRef.current = null;
    recordingScreenStreamRef.current = null;
    recordingAudioContextRef.current = null;
    recordingStopResolverRef.current?.();
    recordingStopResolverRef.current = null;
    setRecordingState((prev) => ({ ...prev, isRecording: false, startedAt: null }));
  }, []);

  const startLocalRecording = useCallback(async (mode: RecordingMode, quality: RecordingQuality) => {
    if (recordingMediaRecorderRef.current && recordingMediaRecorderRef.current.state !== 'inactive') {
      return;
    }

    if (!window.isSecureContext) {
      throw new Error('Для записи требуется HTTPS или localhost');
    }

    if (typeof MediaRecorder === 'undefined') {
      throw new Error('Браузер не поддерживает локальную запись');
    }

    const roomId = useRoomStore.getState().roomId || 'conference';
    const remoteParticipants = Array.from(useParticipantsStore.getState().participants.values());
    let screenStream: MediaStream | null = null;

    if (mode === 'video-audio') {
      if (!navigator.mediaDevices?.getDisplayMedia) {
        throw new Error('Браузер не поддерживает запись экрана');
      }

      screenStream = await navigator.mediaDevices.getDisplayMedia({
        video: {
          frameRate: quality === 'high' ? 30 : quality === 'medium' ? 24 : 15,
          width: quality === 'high' ? { ideal: 1920 } : quality === 'medium' ? { ideal: 1280 } : { ideal: 854 },
          height: quality === 'high' ? { ideal: 1080 } : quality === 'medium' ? { ideal: 720 } : { ideal: 480 },
        },
        audio: true,
        preferCurrentTab: true,
        selfBrowserSurface: 'include',
        surfaceSwitching: 'include',
      } as DisplayMediaStreamOptions);
    }

    const audioContext = new AudioContext();
    const destination = audioContext.createMediaStreamDestination();
    const mixedStream = new MediaStream();

    const connectAudioStream = (stream?: MediaStream | null) => {
      if (!stream) return;

      const audioTracks = stream.getAudioTracks().filter((track) => track.readyState === 'live');
      if (audioTracks.length === 0) return;

      const sourceNode = audioContext.createMediaStreamSource(new MediaStream(audioTracks));
      sourceNode.connect(destination);
    };

    connectAudioStream(localStreamRef.current);
    remoteParticipants.forEach((participant) => connectAudioStream(participant.stream));
    connectAudioStream(screenStream);

    if (mode === 'video-audio') {
      const videoTrack = screenStream?.getVideoTracks()[0];
      if (!videoTrack) {
        screenStream?.getTracks().forEach((track) => track.stop());
        await audioContext.close().catch(() => {});
        throw new Error('Не удалось получить видео экрана для записи');
      }
      mixedStream.addTrack(videoTrack);
    }

    destination.stream.getAudioTracks().forEach((track) => mixedStream.addTrack(track));

    const mimeCandidates = mode === 'video-audio'
      ? ['video/webm;codecs=vp9,opus', 'video/webm;codecs=vp8,opus', 'video/webm']
      : ['audio/webm;codecs=opus', 'audio/webm'];
    const mimeType = mimeCandidates.find((candidate) => MediaRecorder.isTypeSupported(candidate)) || '';

    const recorder = new MediaRecorder(
      mixedStream,
      mimeType
        ? {
            mimeType,
            videoBitsPerSecond: mode === 'video-audio'
              ? quality === 'high'
                ? 6000000
                : quality === 'medium'
                  ? 3500000
                  : 1500000
              : undefined,
            audioBitsPerSecond: quality === 'high' ? 192000 : quality === 'medium' ? 128000 : 96000,
          }
        : undefined
    );

    const cleanupRecordingResources = () => {
      mixedStream.getTracks().forEach((track) => track.stop());
      screenStream?.getTracks().forEach((track) => track.stop());
      audioContext.close().catch(() => {});
      recordingMediaRecorderRef.current = null;
      recordingStreamRef.current = null;
      recordingScreenStreamRef.current = null;
      recordingAudioContextRef.current = null;
    };

    recordingChunksRef.current = [];
    recordingMediaRecorderRef.current = recorder;
    recordingStreamRef.current = mixedStream;
    recordingScreenStreamRef.current = screenStream;
    recordingAudioContextRef.current = audioContext;

    recorder.ondataavailable = (event) => {
      if (event.data && event.data.size > 0) {
        recordingChunksRef.current.push(event.data);
      }
    };

    recorder.onerror = () => {
      cleanupRecordingResources();
      recordingStopResolverRef.current?.();
      recordingStopResolverRef.current = null;
      setRecordingState((prev) => ({ ...prev, isRecording: false, startedAt: null }));
      toast.error('Ошибка локальной записи');
    };

    recorder.onstop = () => {
      const blobType = mimeType || (mode === 'video-audio' ? 'video/webm' : 'audio/webm');
      if (recordingChunksRef.current.length > 0) {
        const blob = new Blob(recordingChunksRef.current, { type: blobType });
        const url = URL.createObjectURL(blob);
        const link = document.createElement('a');
        const timestamp = new Date().toISOString().replace(/[:.]/g, '-');
        link.href = url;
        link.download = `${roomId}-${mode === 'video-audio' ? 'screen-recording' : 'audio-recording'}-${timestamp}.webm`;
        document.body.appendChild(link);
        link.click();
        link.remove();
        setTimeout(() => URL.revokeObjectURL(url), 60000);
      }

      recordingChunksRef.current = [];
      cleanupRecordingResources();
      recordingStopResolverRef.current?.();
      recordingStopResolverRef.current = null;
      setRecordingState((prev) => ({ ...prev, isRecording: false, startedAt: null }));
      toast.success('Запись сохранена');
    };

    const screenVideoTrack = screenStream?.getVideoTracks()[0];
    if (screenVideoTrack) {
      screenVideoTrack.onended = () => {
        stopLocalRecording();
      };
    }

    recorder.start(1000);
    setRecordingState({
      isRecording: true,
      mode,
      quality,
      startedAt: Date.now(),
    });
    toast.success(mode === 'video-audio' ? 'Запись экрана началась' : 'Запись аудио началась');
  }, [stopLocalRecording]);

  const recoverTransports = useCallback(async (source: string) => {
    if (!mediasoup || !socket) return;
    if (!socket.connected) return;
    if (isRejoiningRef.current || isRecoveringTransportRef.current) return;

    isRecoveringTransportRef.current = true;
    try {
      console.log('[Room] Recovering transports due to:', source);
      toast.loading('Восстановление транспорта...', { id: 'transport-recover' });

      const savedAudioEnabled = audioEnabledRef.current;
      const savedVideoEnabled = videoEnabledRef.current;

      await mediasoup.reset();

      let workingStream = localStreamRef.current;
      const audioTrack = workingStream?.getAudioTracks()[0];
      const videoTrack = workingStream?.getVideoTracks()[0];
      const audioEnded = audioTrack?.readyState === 'ended';
      const videoEnded = videoTrack?.readyState === 'ended';

      if (!workingStream || audioEnded || videoEnded) {
        console.log('[Room] Getting fresh media stream for recovery');
        workingStream = await getUserMedia(
          audioDeviceId || undefined,
          videoDeviceId || undefined,
          savedAudioEnabled,
          savedVideoEnabled
        );
        setLocalStream(workingStream);
        localStreamRef.current = workingStream;
        updateLocalParticipant({ stream: workingStream });
      }

      const freshAudioTrack = workingStream.getAudioTracks()[0];
      const freshVideoTrack = workingStream.getVideoTracks()[0];

      if (freshAudioTrack && savedAudioEnabled) {
        await mediasoup.produce(freshAudioTrack, { kind: 'audio' });
      }

      if (freshVideoTrack && savedVideoEnabled) {
        await mediasoup.produce(freshVideoTrack, { kind: 'video' });
      }

      syncLocalMediaState(savedAudioEnabled, savedVideoEnabled);

      await resyncProducersRef.current();
      toast.success('Транспорт восстановлен', { id: 'transport-recover' });
    } catch (error) {
      console.error('[Room] Failed to recover transports:', error);
      toast.error('Не удалось восстановить транспорт', { id: 'transport-recover' });
    } finally {
      isRecoveringTransportRef.current = false;
    }
  }, [mediasoup, socket, audioDeviceId, videoDeviceId, setLocalStream, updateLocalParticipant, syncLocalMediaState]);

  useEffect(() => {
    if (!mediasoup) return;

    mediasoup.setOnTransportFailure((direction, state) => {
      recoverTransports(`transport-${direction}-${state}`);
    });

    return () => {
      mediasoup.setOnTransportFailure(undefined);
    };
  }, [mediasoup, recoverTransports]);

  // Функция для перехода в лобби при потере соединения
  const goToLobby = useCallback((roomId: string, showError: boolean = true) => {
    console.log('[Room] Going to lobby for room:', roomId);

    void (async () => {
      await finalizeLocalRecording();

      // Полная очистка соединения
      cleanupConnection();

      // Очищаем состояние комнаты
      setJoinedRoom(false);
      setInLobby(true);

      // Переходим в лобби комнаты
      navigate(`/room/${roomId}`);

      if (showError) {
        toast.error('Соединение потеряно. Вы перенаправлены в лобби.');
      }
    })();
  }, [cleanupConnection, finalizeLocalRecording, navigate, setJoinedRoom, setInLobby]);

  const joinRoom = useCallback(
    async (roomId: string, displayName: string, password?: string, overrideAudio?: boolean, overrideVideo?: boolean, createRoom?: boolean) => {
      try {
        // Сохраняем roomId в store для доступа из других компонентов (например, Chat)
        setRoomId(roomId);
        
        // Используем переданные значения если они есть, иначе берем из store
        const finalAudioEnabled = overrideAudio !== undefined ? overrideAudio : audioEnabled;
        const finalVideoEnabled = overrideVideo !== undefined ? overrideVideo : videoEnabled;
        const userId = getOrCreateUserId();
        
        console.log('[Room] Joining with audio:', finalAudioEnabled, 'video:', finalVideoEnabled);
        
        // Сохраняем данные для возможного переподключения
        rejoinDataRef.current = {
          roomId,
          userId,
          displayName,
          password,
          audioEnabled: finalAudioEnabled,
          videoEnabled: finalVideoEnabled,
        };
        console.log('[Room] Saved rejoin data:', rejoinDataRef.current);

        if (socket) {
          console.log('[Room] Socket already exists, closing it before creating new one');
          socket.forceStop();
          setSocket(null);
        }

        // Создаем socket manager
        const socketManager = new SocketManagerSimple(
          import.meta.env.VITE_WS_URL || window.location.origin
        );

        const peerStreams = peerStreamsRef.current;
        peerStreams.clear();

        // Настраиваем обработчики событий соединения
        socketManager.setConnectionEvents({
          onStatusChange: (status) => {
            // Проверяем что данные для переподключения еще актуальны
            if (!rejoinDataRef.current) {
              console.log('[Connection] Status changed but no rejoin data, ignoring');
              return;
            }
            console.log('[Connection] Status changed:', status);
            setConnectionStatus(status);
          },
          onReconnectAttempt: (attempt, maxAttempts) => {
            // Проверяем что данные для переподключения еще актуальны
            if (!rejoinDataRef.current) {
              console.log('[Connection] Reconnect attempt but no rejoin data, stopping');
              socketManager.forceStop();
              return;
            }
            console.log(`[Connection] Reconnect attempt ${attempt}/${maxAttempts}`);
            setReconnectAttempt(attempt, maxAttempts);
            toast.loading(`Переподключение... (${attempt}/${maxAttempts})`, { id: 'reconnect' });
          },
          onReconnected: async () => {
            console.log('[Connection] onReconnected called');
            console.log('[Connection] rejoinData:', !!rejoinDataRef.current);
            console.log('[Connection] isRejoining:', isRejoiningRef.current);
            console.log('[Connection] socket.connected:', socketManager.connected);
            console.log('[Connection] socket.id:', socketManager.id);
            console.log('[Connection] mediasoupRef:', !!mediasoupRef.current);
            console.log('[Connection] localStreamRef:', !!localStreamRef.current);
            
            toast.dismiss('reconnect');
            
            // Проверяем что данные для переподключения еще актуальны и не идет процесс переподключения
            if (!rejoinDataRef.current) {
              console.log('[Connection] No rejoin data, skipping rejoin');
              return;
            }
            
            if (isRejoiningRef.current) {
              console.log('[Connection] Already rejoining, skipping');
              return;
            }

            if (isRecoveringTransportRef.current) {
              const waitStartedAt = Date.now();
              while (isRecoveringTransportRef.current && Date.now() - waitStartedAt < 5000) {
                await new Promise((resolve) => setTimeout(resolve, 100));
              }

              if (isRecoveringTransportRef.current) {
                console.error('[Connection] Transport recovery is still running, aborting rejoin');
                if (rejoinDataRef.current) {
                  goToLobby(rejoinDataRef.current.roomId);
                }
                return;
              }
            }
            
            // Проверяем что сокет действительно подключен
            if (!socketManager.connected) {
              console.log('[Connection] Socket not connected yet, waiting...');
              try {
                await socketManager.waitForConnection(5000);
                console.log('[Connection] Socket connection confirmed after wait');
              } catch (err) {
                console.error('[Connection] Failed to wait for connection:', err);
                if (rejoinDataRef.current) {
                  goToLobby(rejoinDataRef.current.roomId);
                }
                return;
              }
            }
            
            isRejoiningRef.current = true;
            const currentRejoinData = { ...rejoinDataRef.current }; // Копируем данные
            const currentMediasoup = mediasoupRef.current;
            const currentLocalStream = localStreamRef.current;
            
            try {
              toast.loading('Восстановление сессии...', { id: 'rejoin' });
              console.log('[Connection] Sending rejoin request to room:', currentRejoinData.roomId);
              
              // Переприсоединяемся к комнате с сохраненными статусами медиа
              const savedAudioEnabled = currentRejoinData.audioEnabled;
              const savedVideoEnabled = currentRejoinData.videoEnabled;
              console.log('[Connection] Using saved media states - audio:', savedAudioEnabled, 'video:', savedVideoEnabled);
              
              const rejoinResponse = await socketManager.emit('join', {
                roomId: currentRejoinData.roomId,
                userId: currentRejoinData.userId,
                displayName: currentRejoinData.displayName,
                password: currentRejoinData.password,
                audioEnabled: savedAudioEnabled,
                videoEnabled: savedVideoEnabled,
              });
              
              console.log('[Connection] Rejoin response:', rejoinResponse);
              
              // Проверяем что данные все еще актуальны после await
              if (!rejoinDataRef.current) {
                console.log('[Connection] Rejoin data cleared during rejoin, aborting');
                toast.dismiss('rejoin');
                return;
              }
              
              if (!rejoinResponse.success) {
                throw new Error('Failed to rejoin room');
              }
              
              // Пересоздаем mediasoup транспорты и producers
              if (currentMediasoup) {
                console.log('[Connection] Resetting mediasoup transports...');
                await currentMediasoup.reset();
                
                // Проверяем состояние треков
                let workingStream = currentLocalStream;
                const audioTrack = workingStream?.getAudioTracks()[0];
                const videoTrack = workingStream?.getVideoTracks()[0];
                
                const audioEnded = audioTrack?.readyState === 'ended';
                const videoEnded = videoTrack?.readyState === 'ended';
                
                console.log('[Connection] Track states - audio:', audioTrack?.readyState, 'video:', videoTrack?.readyState);
                
                // Если треки ended, получаем новый медиа поток
                if (!workingStream || audioEnded || videoEnded) {
                  console.log('[Connection] Tracks ended or missing, getting fresh media stream...');
                  console.log('[Connection] Saved media states - audio:', savedAudioEnabled, 'video:', savedVideoEnabled);
                  try {
                    workingStream = await getUserMedia(
                      audioDeviceId || undefined, 
                      videoDeviceId || undefined,
                      savedAudioEnabled,
                      savedVideoEnabled
                    );
                    setLocalStream(workingStream);
                    localStreamRef.current = workingStream;
                    
                    // Обновляем stream у локального участника
                    updateLocalParticipant({ stream: workingStream });
                    
                    console.log('[Connection] Fresh media stream obtained');
                  } catch (err) {
                    console.error('[Connection] Failed to get fresh media stream:', err);
                    throw new Error('Failed to get media stream');
                  }
                }
                
                // Пересоздаем producers для локального медиа
                const freshAudioTrack = workingStream.getAudioTracks()[0];
                const freshVideoTrack = workingStream.getVideoTracks()[0];
                
                if (freshAudioTrack && savedAudioEnabled) {
                  console.log('[Connection] Recreating audio producer...');
                  await currentMediasoup.produce(freshAudioTrack, { kind: 'audio' });
                }
                
                if (freshVideoTrack && savedVideoEnabled) {
                  console.log('[Connection] Recreating video producer...');
                  await currentMediasoup.produce(freshVideoTrack, { kind: 'video' });
                }
                
                syncLocalMediaState(savedAudioEnabled, savedVideoEnabled);
                
                // Consume producers других участников
                // Фильтруем свои старые producers (от старого socket.id)
                if (rejoinResponse.peers && rejoinResponse.peers.length > 0) {
                  peerStreams.clear();
                  console.log('[Connection] Current socket.id:', socketManager.id);
                  console.log('[Connection] Peers in response:', rejoinResponse.peers.map((p: any) => p.id));
                  
                  // Фильтруем peers - исключаем свой новый socket.id на случай если сервер вернул его в списке
                  const otherPeers = rejoinResponse.peers.filter((p: any) => p.id !== socketManager.id);
                  console.log('[Connection] Consuming producers from', otherPeers.length, 'other peers');
                  
                  for (const peer of otherPeers) {
                    if (peer.producers && peer.producers.length > 0) {
                      for (const producer of peer.producers) {
                        try {
                          await consumeRemoteProducer(
                            currentMediasoup,
                            socketManager,
                            peer.id,
                            producer.id,
                            producer.kind,
                            producer.appData
                          );
                        } catch (err) {
                          console.error(`[Connection] Failed to consume producer ${producer.id}:`, err);
                        }
                      }
                    }
                  }
                }
                
                // Обновляем локального участника с новым socket.id
                updateLocalParticipant({ id: socketManager.id! });
                await resyncProducersRef.current();
                
                console.log('[Connection] Media infrastructure restored');
              } else {
                console.warn('[Connection] No mediasoup or localStream available for restore');
              }
              
              toast.success('Соединение восстановлено!', { id: 'rejoin' });
              console.log('[Connection] Rejoin successful');
            } catch (error) {
              console.error('[Connection] Failed to rejoin room:', error);
              // Проверяем актуальность данных перед переходом в лобби
              if (rejoinDataRef.current) {
                toast.error('Не удалось восстановить сессию', { id: 'rejoin' });
                goToLobby(currentRejoinData.roomId);
              }
            } finally {
              isRejoiningRef.current = false;
            }
          },
          onReconnectFailed: () => {
            console.log('[Connection] Reconnect failed, rejoinData:', !!rejoinDataRef.current);
            toast.dismiss('reconnect');
            
            // Проверяем что данные для переподключения еще актуальны
            if (rejoinDataRef.current) {
              const roomIdToNavigate = rejoinDataRef.current.roomId;
              toast.error('Не удалось восстановить соединение');
              goToLobby(roomIdToNavigate);
            }
          },
          onDisconnected: (reason) => {
            console.log('[Connection] Disconnected:', reason, 'rejoinData:', !!rejoinDataRef.current);
            
            // Проверяем что данные для переподключения еще актуальны
            if (!rejoinDataRef.current) {
              console.log('[Connection] No rejoin data, ignoring disconnect');
              return;
            }
            
            setDisconnectReason(reason);
            
            if (reason === 'io server disconnect') {
              // Сервер принудительно отключил - идем в лобби
              const roomIdToNavigate = rejoinDataRef.current.roomId;
              goToLobby(roomIdToNavigate);
            }
          },
        });
        
        await socketManager.connect();
        setSocket(socketManager);

        // Хранилище для потоков участников уже инициализировано выше

        socketManager.on('roomStateSnapshot', (snapshot: any) => {
          console.log('[Room] Room state snapshot received:', snapshot);
          if (!snapshot || !Array.isArray(snapshot.clients)) return;

          const { participants, localParticipant } = useParticipantsStore.getState();
          const connectedClients = snapshot.clients.filter((client: any) => !!client.socketId);
          const clientsByUserId = new Map<string, any>(
            connectedClients.map((client: any) => [client.userId, client])
          );
          const clientsBySocketId = new Map<string, any>(
            connectedClients.map((client: any) => [client.socketId, client])
          );

          const toRemove: string[] = [];
          for (const [participantId, participant] of participants) {
            if (localParticipant && participantId === localParticipant.id) continue;

            const client = participant.userId
              ? clientsByUserId.get(participant.userId)
              : clientsBySocketId.get(participantId);

            if (!client) {
              toRemove.push(participantId);
              continue;
            }

            if (client.socketId && client.socketId !== participantId) {
              toRemove.push(participantId);
            }
          }

          toRemove.forEach((participantId) => {
            peerStreams.delete(participantId);
            removeParticipant(participantId);
          });

          connectedClients.forEach((client: any) => {
            if (!client.socketId || client.userId === userId) return;
            const existing = participants.get(client.socketId);
            if (!existing) {
              addParticipant({
                id: client.socketId,
                userId: client.userId,
                displayName: client.displayName,
                isHost: client.role === 'admin',
                audioEnabled: client.isMicOn !== false,
                videoEnabled: client.isCameraOn !== false,
                screenShareEnabled: client.isScreenSharing === true,
                raisedHand: false,
                isSpeaking: false,
              });
              const existingStream = peerStreams.get(client.socketId);
              if (existingStream) {
                setParticipantStream(client.socketId, existingStream);
              }
            } else {
              updateParticipant(client.socketId, {
                userId: client.userId,
                displayName: client.displayName,
                isHost: client.role === 'admin',
                audioEnabled: client.isMicOn !== false,
                videoEnabled: client.isCameraOn !== false,
                screenShareEnabled: client.isScreenSharing === true,
              });
              const existingStream = peerStreams.get(client.socketId);
              if (existingStream) {
                setParticipantStream(client.socketId, existingStream);
              }
            }
          });

          if (localParticipant && localParticipant.userId) {
            const localClient = snapshot.clients.find((client: any) => client.userId === localParticipant.userId);
            if (localClient) {
              syncLocalMediaState(localClient.isMicOn !== false, localClient.isCameraOn !== false);
              updateLocalParticipant({
                isHost: localClient.role === 'admin',
                audioEnabled: localClient.isMicOn !== false,
                videoEnabled: localClient.isCameraOn !== false,
                screenShareEnabled: localClient.isScreenSharing === true
              });
            }
          }
        });

        socketManager.on('userStateUpdated', (clientState: any) => {
          if (!clientState || !clientState.userId) return;
          const { participants, localParticipant } = useParticipantsStore.getState();
          const isLocalUser = clientState.userId === userId;

          let participantId: string | null = null;
          for (const [id, participant] of participants) {
            if (participant.userId === clientState.userId) {
              participantId = id;
              break;
            }
          }

          if (!participantId && clientState.socketId) {
            participantId = clientState.socketId;
          }

          if (participantId && clientState.socketId && participantId !== clientState.socketId) {
            peerStreams.delete(participantId);
            removeParticipant(participantId);
            participantId = clientState.socketId;
          }

          const updates = {
            userId: clientState.userId,
            displayName: clientState.displayName,
            isHost: clientState.role === 'admin',
            audioEnabled: clientState.isMicOn !== false,
            videoEnabled: clientState.isCameraOn !== false,
            screenShareEnabled: clientState.isScreenSharing === true,
          } as const;

          if (localParticipant && localParticipant.userId === clientState.userId) {
            syncLocalMediaState(clientState.isMicOn !== false, clientState.isCameraOn !== false);
            updateLocalParticipant(updates);
          }

          if (isLocalUser && !localParticipant) {
            return;
          }

          if (isLocalUser) {
            return;
          }

          if (!clientState.socketId || clientState.connectionQuality === 'offline') {
            if (participantId) {
              peerStreams.delete(participantId);
              removeParticipant(participantId);
            }
            return;
          }

          if (participantId && participants.has(participantId)) {
            updateParticipant(participantId, updates);
            const existingStream = peerStreams.get(participantId);
            if (existingStream) {
              setParticipantStream(participantId, existingStream);
            }
          } else if (clientState.socketId) {
            addParticipant({
              id: clientState.socketId,
              userId: clientState.userId,
              displayName: clientState.displayName,
              isHost: clientState.role === 'admin',
              audioEnabled: clientState.isMicOn !== false,
              videoEnabled: clientState.isCameraOn !== false,
              screenShareEnabled: clientState.isScreenSharing === true,
              raisedHand: false,
              isSpeaking: false,
            });
            const existingStream = peerStreams.get(clientState.socketId);
            if (existingStream) {
              setParticipantStream(clientState.socketId, existingStream);
            }
          }
        });

        // Присоединяемся к комнате с текущими статусами медиа
        const joinResponse = await socketManager.emit('join', {
          roomId,
          userId,
          displayName,
          password,
          audioEnabled: finalAudioEnabled,
          videoEnabled: finalVideoEnabled,
          createRoom: createRoom || false,
        });

        if (!joinResponse.success) {
          const error = new Error(joinResponse.error || 'Failed to join room') as Error & { needsPassword?: boolean; roomNotFound?: boolean };
          error.needsPassword = joinResponse.needsPassword === true;
          error.roomNotFound = joinResponse.roomNotFound === true;
          throw error;
        }

        toast.success('Подключено к комнате');
        
        // Сохраняем пароль для возможного возврата в лобби
        setStoredPassword(password || null);

        // Создаем mediasoup client
        const mediasoupClient = new MediasoupClient(socketManager);
        await mediasoupClient.loadDevice();
        setMediasoup(mediasoupClient);
        mediasoupRef.current = mediasoupClient; // Сохраняем ref для доступа в callbacks

        // Создаем транспорты
        await mediasoupClient.createSendTransport();
        await mediasoupClient.createRecvTransport();

        // Получаем медиа поток с учетом переданных статусов
        let stream = await getUserMedia(
          audioDeviceId || undefined, 
          videoDeviceId || undefined,
          finalAudioEnabled,
          finalVideoEnabled
        );
        
        // Применяем размытие фона если включено
        if (blurBackground && finalVideoEnabled) {
          console.log('[Room] Applying background blur to video stream');
          stream = await applyBackgroundBlur(stream, true);
        }
        
        setLocalStream(stream);
        localStreamRef.current = stream; // Сохраняем ref для доступа в callbacks

        // Создаем producers
        const audioTrack = stream.getAudioTracks()[0];
        const videoTrack = stream.getVideoTracks()[0];

        if (audioTrack && finalAudioEnabled) {
          await mediasoupClient.produce(audioTrack, { kind: 'audio' });
        }

        if (videoTrack && finalVideoEnabled) {
          await mediasoupClient.produce(videoTrack, { kind: 'video' });
        }

        // Устанавливаем локального участника с переданными статусами
        setLocalParticipant({
          id: socketManager.id!,
          userId,
          displayName,
          isHost: joinResponse.isHost || false,
          audioEnabled: finalAudioEnabled,
          videoEnabled: finalVideoEnabled,
          screenShareEnabled: false,
          raisedHand: false,
          isSpeaking: false,
          stream,
        });

        // Хранилище для потоков участников создано выше

        // Обрабатываем существующих участников
        console.log('[Room] Processing existing peers:', joinResponse.peers?.length || 0);
        if (joinResponse.peers) {
          for (const peer of joinResponse.peers) {
            console.log(`[Room] Adding existing peer ${peer.id}: audio=${peer.audioEnabled}, video=${peer.videoEnabled}`);
            addParticipant({
              id: peer.id,
              userId: peer.userId,
              displayName: peer.displayName,
              isHost: false,
              audioEnabled: peer.audioEnabled !== false,
              videoEnabled: peer.videoEnabled !== false,
              screenShareEnabled: false,
              raisedHand: false,
              isSpeaking: false,
            });
            
            // Consume существующие producers этого участника
            if (peer.producers && peer.producers.length > 0) {
              console.log(`[Room] Peer ${peer.id} has ${peer.producers.length} producers`);
              for (const producer of peer.producers) {
                console.log(`[Room] Producer ${producer.id}: kind=${producer.kind}, paused=${producer.paused}, appData:`, producer.appData);
                await consumeRemoteProducer(
                  mediasoupClient,
                  socketManager,
                  peer.id,
                  producer.id,
                  producer.kind,
                  producer.appData
                );
              }
            }
          }
        }

        // Слушаем события
        socketManager.on('peerJoined', (data: any) => {
          console.log('[Room] Peer joined event received:', JSON.stringify(data));
          console.log(`[Room] New peer ${data.peerId}: audio=${data.audioEnabled}, video=${data.videoEnabled}`);
          // Fallback: добавляем участника только если его еще нет (истина - snapshot/userStateUpdated)
          const { participants } = useParticipantsStore.getState();
          const existing = participants.get(data.peerId) || Array.from(participants.values())
            .find((participant) => participant.userId && participant.userId === data.userId);
          if (!existing) {
            addParticipant({
              id: data.peerId,
              userId: data.userId,
              displayName: data.displayName,
              isHost: false,
              audioEnabled: data.audioEnabled !== false,
              videoEnabled: data.videoEnabled !== false,
              screenShareEnabled: false,
              raisedHand: false,
              isSpeaking: false,
            });
          }
          toast.success(`${data.displayName} присоединился`);
        });

        // Обработка новых producers от других участников
        socketManager.on('newProducer', async (data: any) => {
          console.log('[Room] New producer event received:', {
            peerId: data.peerId,
            producerId: data.producerId,
            kind: data.kind,
            appData: data.appData
          });
          
          // Проверяем что mediasoup и socket доступны
          if (!mediasoupClient || !socketManager) {
            console.warn('[Room] Mediasoup or socket not available for consuming producer');
            return;
          }
          
          // Consume новый producer
          await consumeRemoteProducer(
            mediasoupClient,
            socketManager,
            data.peerId,
            data.producerId,
            data.kind,
            data.appData
          );
        });

        socketManager.on('peerClosed', (data: any) => {
          console.log('[Room] Peer closed:', data);
          
          const { participants } = useParticipantsStore.getState();
          let participantId = data.peerId;

          if (data.userId) {
            for (const [id, participant] of participants) {
              if (participant.userId === data.userId) {
                participantId = id;
                break;
              }
            }
          }
          
          // Явно закрываем все consumers этого участника по producerId
          const participant = participants.get(participantId);
          if (participant) {
            const producerIds = [
              participant.audioProducerId,
              participant.videoProducerId,
              participant.screenProducerId
            ].filter(Boolean);
            
            if (producerIds.length > 0) {
              const consumers = mediasoupClient.getConsumers();
              let closedCount = 0;
              for (const [consumerId, consumer] of consumers) {
                if (producerIds.includes(consumer.producerId)) {
                  try {
                    mediasoupClient.closeConsumer(consumerId);
                    closedCount++;
                  } catch (err) {
                    console.warn(`[Room] Error closing consumer ${consumerId}:`, err);
                  }
                }
              }
              if (closedCount > 0) {
                console.log(`[Room] Closed ${closedCount} consumers for leaving peer ${participantId}`);
              }
            }
          }
          
          // Удаляем stream этого участника
          peerStreams.delete(participantId);
          
          removeParticipant(participantId);
          toast(`Participant left`);
        });

        // Обработка закрытия producer
        socketManager.on('producerClosed', (data: any) => {
          console.log('[Room] Producer closed:', data);
          // Закрываем consumer для этого producer
          const consumers = mediasoupClient.getConsumers();
          for (const [consumerId, consumer] of consumers) {
            if (consumer.producerId === data.producerId) {
              mediasoupClient.closeConsumer(consumerId);
              console.log(`[Room] Closed consumer ${consumerId} for producer ${data.producerId}`);
              break;
            }
          }
        });

        // Обработка паузы/возобновления producer (для обновления UI статуса)
        socketManager.on('producerPaused', async (data: any) => {
          console.log('[Room] Producer paused:', data);
          
          // Находим и паузим соответствующий consumer
          const consumers = mediasoupClient.getConsumers();
          for (const [, consumer] of consumers) {
            if (consumer.producerId === data.producerId) {
              try {
                await consumer.pause();
                console.log(`[Room] Paused consumer for producer ${data.producerId}`);
              } catch (error) {
                console.error('[Room] Error pausing consumer:', error);
              }
              break;
            }
          }
          
          // Обновляем UI-состояние
          const kind = data.kind;
          if (kind) {
            updateParticipant(data.peerId, {
              [kind === 'audio' ? 'audioEnabled' : 'videoEnabled']: false
            });
            console.log(`[Room] Updated ${data.peerId} ${kind}Enabled = false`);
          } else {
            // Fallback: определяем тип по consumer
            for (const [, consumer] of consumers) {
              if (consumer.producerId === data.producerId) {
                updateParticipant(data.peerId, {
                  [consumer.kind === 'audio' ? 'audioEnabled' : 'videoEnabled']: false
                });
                console.log(`[Room] Updated ${data.peerId} ${consumer.kind}Enabled = false (fallback)`);
                break;
              }
            }
          }
        });

        socketManager.on('producerResumed', async (data: any) => {
          console.log('[Room] Producer resumed:', data);
          
          // Находим и резюмим соответствующий consumer
          const consumers = mediasoupClient.getConsumers();
          for (const [, consumer] of consumers) {
            if (consumer.producerId === data.producerId) {
              try {
                await consumer.resume();
                console.log(`[Room] Resumed consumer for producer ${data.producerId}`);
              } catch (error) {
                console.error('[Room] Error resuming consumer:', error);
              }
              break;
            }
          }
          
          // Обновляем UI-состояние
          const kind = data.kind;
          if (kind) {
            updateParticipant(data.peerId, {
              [kind === 'audio' ? 'audioEnabled' : 'videoEnabled']: true
            });
            console.log(`[Room] Updated ${data.peerId} ${kind}Enabled = true`);
          } else {
            // Fallback: определяем тип по consumer
            for (const [, consumer] of consumers) {
              if (consumer.producerId === data.producerId) {
                updateParticipant(data.peerId, {
                  [consumer.kind === 'audio' ? 'audioEnabled' : 'videoEnabled']: true
                });
                console.log(`[Room] Updated ${data.peerId} ${consumer.kind}Enabled = true (fallback)`);
                break;
              }
            }
          }
        });

        socketManager.on('chatMessage', (data: any) => {
          const isOwnMessage = data.senderId === socketManager.id;
          addChatMessage({
            id: data.id || Date.now().toString(),
            senderId: data.senderId,
            senderName: data.senderName,
            text: data.text,
            timestamp: data.timestamp,
            type: data.type || 'text',
          }, isOwnMessage);
        });

        socketManager.on('chatMessageDeleted', (data: any) => {
          if (!data?.messageId) {
            return;
          }

          removeChatMessage(data.messageId);
        });

        // Централизованное управление демонстрацией экрана
        socketManager.on('screenShareStarted', (data: any) => {
          console.log('[Room] Screen share started by peer:', data);
          
          // Обновляем состояние участника который начал демонстрацию
          updateParticipant(data.peerId, { screenShareEnabled: true });
          
          // Показываем уведомление (кроме себя)
          if (data.peerId !== socketManager.id) {
            toast(`${data.displayName} started screen sharing`);
          }
        });

        socketManager.on('screenShareStopped', (data: any) => {
          console.log('[Room] Screen share stopped by peer:', data);
          
          // Очищаем screenStream и сбрасываем флаг демонстрации
          updateParticipant(data.peerId, { 
            screenShareEnabled: false,
            screenStream: undefined 
          });
          setParticipantScreenStream(data.peerId, undefined);
          
          // Показываем уведомление (кроме себя)
          if (data.peerId !== socketManager.id) {
            toast('Screen sharing ended');
          }
        });

        // Принудительная остановка демонстрации (когда другой пользователь начал свою)
        socketManager.on('forceStopScreenShare', (data: any) => {
          console.log('[Room] Force stop screen share:', data);
          
          // Останавливаем локальную демонстрацию
          if (screenStreamRef.current) {
            screenStreamRef.current.getTracks().forEach(track => track.stop());
            screenStreamRef.current = null;
          }
          
          // Закрываем screenShare producers
          if (mediasoupClient) {
            const producers = mediasoupClient.getProducers();
            producers.forEach((producer) => {
              if (producer.appData?.screenShare === true) {
                mediasoupClient.closeProducer(producer.id);
              }
            });
          }
          
          // Обновляем состояние
          setScreenShareEnabled(false);
          updateLocalParticipant({ screenShareEnabled: false, screenStream: undefined });
          
          toast(data.reason || 'Screen sharing stopped');
        });

        // Применяем уровень битрейта из roomState (по умолчанию low)
        const initialBitrateLevel = joinResponse.roomState?.bitrateLevel || 'low';
        console.log(`[Room] Applying initial bitrate level: ${initialBitrateLevel}`);
        setBitrateLevelState(initialBitrateLevel);
        mediasoupClient.setVideoBitrate(initialBitrateLevel).catch((error) => {
          console.error('[Room] Failed to apply initial bitrate level:', error);
        });

        // Обрабатываем roomState если есть активная демонстрация при подключении
        if (joinResponse.roomState?.activeScreenShare) {
          const { peerId, producerId } = joinResponse.roomState.activeScreenShare;
          console.log('[Room] Active screen share found on join:', { peerId, producerId });
          
          // Демонстрация экрана уже идет - нужно подключиться к ней
          // Producer уже должен быть в списке peers.producers
          updateParticipant(peerId, { screenShareEnabled: true });
        }

        // Обработчик принудительного отключения микрофона администратором
        socketManager.on('forceMute', async () => {
          console.log('[Room] Force muted by host');
          
          // Используем актуальное состояние из store
          const { localStream: currentStream } = useMediaStore.getState();
          const { updateLocalParticipant: updateLocal } = useParticipantsStore.getState();
          
          if (currentStream) {
            const audioTrack = currentStream.getAudioTracks()[0];
            if (audioTrack) {
              audioTrack.enabled = false;
              audioEnabledRef.current = false;
              if (rejoinDataRef.current) {
                rejoinDataRef.current.audioEnabled = false;
              }
              setAudioEnabled(false);
              updateLocal({ audioEnabled: false });
              
              // Паузим producer если есть
              const producers = mediasoupClient.getProducers();
              const audioProducer = Array.from(producers.values()).find(p => p.kind === 'audio');
              if (audioProducer) {
                try {
                  await mediasoupClient.pauseProducer(audioProducer.id);
                } catch (error) {
                  console.error('[Room] Failed to pause audio producer after forceMute:', error);
                }
              }
              
              toast.error('Ваш микрофон был выключен организатором');
            }
          }
        });

        // Обработчик изменения статуса поднятой руки
        socketManager.on('participantRaisedHand', (data: { peerId: string; raisedHand: boolean }) => {
          console.log('[Room] Participant raised hand:', data);
          // Если это локальный участник - обновляем localParticipant
          const { localParticipant } = useParticipantsStore.getState();
          if (localParticipant && data.peerId === localParticipant.id) {
            updateLocalParticipant({ raisedHand: data.raisedHand });
          } else {
            updateParticipant(data.peerId, { raisedHand: data.raisedHand });
          }
        });

        // Обработчик изменения уровня битрейта
        socketManager.on('bitrateLevelChanged', (data: { level: 'standard' | 'low'; changedBy: string }) => {
          console.log('[Room] Bitrate level changed:', data);
          setBitrateLevelState(data.level);
          
          // Применяем новый битрейт к видео producers
          mediasoupClient.setVideoBitrate(data.level).catch((error) => {
            console.error('[Room] Failed to apply bitrate change:', error);
          });
          
          toast(data.level === 'low' ? '📉 Видео: режим экономии трафика' : '📈 Видео: стандартный режим');
        });

        setJoinedRoom(true);
        setInLobby(false);

        // Синхронизация участников и producers после полной инициализации (catch-up для гонки)
        // Запрашиваем актуальный список через небольшую задержку
        setTimeout(async () => {
          try {
            console.log('[Room] Syncing peers and producers (catch-up)...');
            const response = await socketManager.emit('getProducers', {});
            if (!response.success) {
              console.error('[Room] Failed to get producers:', response.error);
              return;
            }
            
            // Сначала добавляем недостающих участников
            const { participants } = useParticipantsStore.getState();
            const existingPeerIds = new Set(participants.keys());
            
            if (response.peers && response.peers.length > 0) {
              for (const peer of response.peers) {
                if (!existingPeerIds.has(peer.id)) {
                  console.log(`[Room] Adding missing peer ${peer.id} (${peer.displayName})`);
                  addParticipant({
                    id: peer.id,
                    userId: peer.userId,
                    displayName: peer.displayName,
                    isHost: false,
                    audioEnabled: peer.audioEnabled !== false,
                    videoEnabled: peer.videoEnabled !== false,
                    screenShareEnabled: false,
                    raisedHand: false,
                    isSpeaking: false,
                  });
                }
              }
            }
            
            // Затем синхронизируем producers
            if (response.producers && response.producers.length > 0) {
              console.log(`[Room] Found ${response.producers.length} producers to sync`);
              
              const existingConsumers = mediasoupClient.getConsumers();
              const consumedProducerIds = new Set<string>();
              existingConsumers.forEach((consumer) => {
                consumedProducerIds.add(consumer.producerId);
              });
              
              for (const producer of response.producers) {
                if (consumedProducerIds.has(producer.producerId)) {
                  console.log(`[Room] Already consuming producer ${producer.producerId}, skipping`);
                  continue;
                }
                
                console.log(`[Room] Syncing producer ${producer.producerId} (${producer.kind}) from ${producer.peerId}`);
                await consumeRemoteProducer(
                  mediasoupClient,
                  socketManager,
                  producer.peerId,
                  producer.producerId,
                  producer.kind,
                  producer.appData
                );
              }
              console.log('[Room] Producer sync complete');
            } else {
              console.log('[Room] No new producers to sync');
            }
          } catch (error) {
            console.error('[Room] Failed to sync:', error);
          }
        }, 1000); // 1 секунда задержки для catch-up

      } catch (error: any) {
        console.error('Failed to join room:', error);
        
        // Очищаем данные для переподключения чтобы не было попыток reconnect из лобби
        rejoinDataRef.current = null;
        console.log('[Room] Cleared rejoin data due to join error');
        
        // Закрываем socket если он был создан
        if (socket) {
          socket.forceStop();
          setSocket(null);
        }
        
        toast.error(error.message || 'Не удалось подключиться к комнате');
        throw error;
      }
    },
    [audioEnabled, videoEnabled, audioDeviceId, videoDeviceId, socket]
  );

  const leaveRoom = useCallback(() => {
    void (async () => {
      await finalizeLocalRecording();

      // Останавливаем все треки
      if (localStream) {
        localStream.getTracks().forEach((track) => track.stop());
        setLocalStream(null);
      }

      // Закрываем mediasoup
      if (mediasoup) {
        mediasoup.close();
        setMediasoup(null);
      }

      // Отключаемся от сокета
      if (socket) {
        socket.disconnect();
        setSocket(null);
      }

      // Очищаем состояние и возвращаемся в лобби
      useMediaStore.getState().reset();
      setJoinedRoom(false);
      setInLobby(false);
      navigate('/');
    })();
  }, [finalizeLocalRecording, localStream, mediasoup, socket, navigate, setInLobby, setJoinedRoom, setLocalStream]);

  const toggleAudio = useCallback(async () => {
    if (!localStream || !mediasoup) return;
    if (isRejoiningRef.current || isRecoveringTransportRef.current) return;

    const audioTrack = localStream.getAudioTracks()[0];
    if (!audioTrack) return;

    const previousEnabled = audioEnabledRef.current;
    const newEnabled = !audioEnabled;
    syncLocalMediaState(newEnabled, videoEnabledRef.current);

    // Проверяем наличие audio producer
    const producers = mediasoup.getProducers();
    const audioProducer = Array.from(producers.values()).find(p => p.kind === 'audio');

    if (audioProducer) {
      // Producer существует - просто паузим/возобновляем
      try {
        if (newEnabled) {
          await mediasoup.resumeProducer(audioProducer.id);
        } else {
          await mediasoup.pauseProducer(audioProducer.id);
        }
      } catch (error) {
        console.error('[Audio] Failed to update audio producer state:', error);
        syncLocalMediaState(previousEnabled, videoEnabledRef.current);
        toast.error('Не удалось изменить состояние микрофона');
        return;
      }
    } else if (newEnabled) {
      // Producer не существует и мы включаем - создаем новый
      try {
        await mediasoup.produce(audioTrack, { kind: 'audio' });
        console.log('[Audio] Created new audio producer');
      } catch (error) {
        console.error('[Audio] Failed to create audio producer:', error);
        syncLocalMediaState(previousEnabled, videoEnabledRef.current);
        toast.error('Не удалось включить микрофон');
        return;
      }
    }

    toast.success(newEnabled ? 'Микрофон включен' : 'Микрофон выключен');
  }, [localStream, mediasoup, audioEnabled, syncLocalMediaState]);

  const toggleVideo = useCallback(async () => {
    if (!localStream || !mediasoup) return;
    if (isRejoiningRef.current || isRecoveringTransportRef.current) return;

    const videoTrack = localStream.getVideoTracks()[0];
    if (!videoTrack) return;

    const previousEnabled = videoEnabledRef.current;
    const newEnabled = !videoEnabled;
    syncLocalMediaState(audioEnabledRef.current, newEnabled);

    // Проверяем наличие video producer
    const producers = mediasoup.getProducers();
    const videoProducer = Array.from(producers.values()).find(p => p.kind === 'video' && !p.appData?.screenShare);

    if (videoProducer) {
      // Producer существует - просто паузим/возобновляем
      try {
        if (newEnabled) {
          await mediasoup.resumeProducer(videoProducer.id);
        } else {
          await mediasoup.pauseProducer(videoProducer.id);
        }
      } catch (error) {
        console.error('[Video] Failed to update video producer state:', error);
        syncLocalMediaState(audioEnabledRef.current, previousEnabled);
        toast.error('Не удалось изменить состояние камеры');
        return;
      }
    } else if (newEnabled) {
      // Producer не существует и мы включаем - создаем новый
      try {
        await mediasoup.produce(videoTrack, { kind: 'video' });
        console.log('[Video] Created new video producer');
      } catch (error) {
        console.error('[Video] Failed to create video producer:', error);
        syncLocalMediaState(audioEnabledRef.current, previousEnabled);
        toast.error('Не удалось включить камеру');
        return;
      }
    }

    toast.success(newEnabled ? 'Камера включена' : 'Камера выключена');
  }, [localStream, mediasoup, videoEnabled, syncLocalMediaState]);

  const startScreenShare = useCallback(async () => {
    console.log('[ScreenShare] startScreenShare called', { 
      hasMediasoup: !!mediasoup, 
      screenShareEnabled 
    });
    
    if (!mediasoup) {
      console.warn('[ScreenShare] No mediasoup client');
      return;
    }

    // Если уже идет демонстрация - останавливаем
    if (screenShareEnabled) {
      console.log('[ScreenShare] Already sharing, stopping first');
      stopScreenShare();
      return;
    }

    try {
      console.log('[ScreenShare] Requesting display media...');
      const screenStream = await navigator.mediaDevices.getDisplayMedia({
        video: {
          width: 1920,
          height: 1080,
          frameRate: 30,
        },
        audio: false,
      });

      console.log('[ScreenShare] Display media obtained:', {
        streamId: screenStream.id,
        tracks: screenStream.getTracks().map(t => ({
          kind: t.kind,
          label: t.label,
          readyState: t.readyState
        }))
      });

      // Сохраняем ссылку на stream для остановки
      screenStreamRef.current = screenStream;

      const screenTrack = screenStream.getVideoTracks()[0];
      console.log('[ScreenShare] Screen track:', {
        kind: screenTrack.kind,
        label: screenTrack.label,
        readyState: screenTrack.readyState
      });
      
      // Передаем appData напрямую (не вложенный объект)
      console.log('[ScreenShare] Calling mediasoup.produce with appData:', { screenShare: true });
      const producer = await mediasoup.produce(screenTrack, { screenShare: true });
      console.log('[ScreenShare] Producer created:', {
        id: producer.id,
        kind: producer.kind,
        appData: producer.appData
      });

      screenTrack.onended = () => {
        console.log('[ScreenShare] Screen track ended by user');
        stopScreenShare();
      };

      // Обновляем состояние - ВАЖНО: устанавливаем screenStream для локального участника
      setScreenShareEnabled(true);
      updateLocalParticipant({ screenShareEnabled: true, screenStream: screenStream });

      toast.success('Демонстрация экрана начата');
      console.log('[ScreenShare] Screen sharing started successfully, screenStream set for local participant');
    } catch (error) {
      console.error('[ScreenShare] Failed to start screen share:', error);
      if ((error as any).name !== 'NotAllowedError') {
        toast.error('Не удалось начать демонстрацию экрана');
      }
    }
  }, [mediasoup, screenShareEnabled, updateLocalParticipant, setScreenShareEnabled]);

  const stopScreenShare = useCallback(() => {
    console.log('stopScreenShare called');
    
    // Останавливаем screen stream напрямую
    if (screenStreamRef.current) {
      console.log('Stopping screen stream tracks');
      screenStreamRef.current.getTracks().forEach(track => {
        console.log('Stopping track:', track.kind, track.readyState);
        track.stop();
      });
      screenStreamRef.current = null;
    }

    // Закрываем screenShare producers
    if (mediasoup) {
      const producers = mediasoup.getProducers();
      producers.forEach((producer) => {
        if (producer.appData?.screenShare === true) {
          console.log('Closing screenShare producer:', producer.id);
          mediasoup.closeProducer(producer.id);
        }
      });
    }

    // Уведомляем сервер об остановке демонстрации
    if (socket) {
      socket.emit('stopScreenShare', {}).catch((err: any) => {
        console.error('Failed to notify server about screen share stop:', err);
      });
    }

    // Обновляем состояние - очищаем screenStream для локального участника
    setScreenShareEnabled(false);
    updateLocalParticipant({ screenShareEnabled: false, screenStream: undefined });

    toast('Screen sharing stopped');
  }, [mediasoup, socket, setScreenShareEnabled, updateLocalParticipant]);

  const muteParticipant = useCallback((participantId: string) => {
    if (!socket) return;
    
    socket.emit('muteParticipant', { participantId }).catch((error) => {
      console.error('Failed to mute participant:', error);
      toast.error('Не удалось отключить микрофон участника');
    });
  }, [socket]);

  const muteAllParticipants = useCallback(() => {
    if (!socket) return;
    
    socket.emit('muteAll', {}).catch((error) => {
      console.error('Failed to mute all participants:', error);
      toast.error('Не удалось отключить микрофоны участников');
    });
  }, [socket]);

  const makeAdmin = useCallback((participantId: string) => {
    if (!socket) return;
    
    socket.emit('makeAdmin', { participantId }).catch((error) => {
      console.error('Failed to make admin:', error);
      toast.error('Не удалось назначить администратора');
    });
  }, [socket]);

  const kickUser = useCallback((participantId: string) => {
    if (!socket) return;
    
    socket.emit('kickUser', { participantId }).catch((error) => {
      console.error('Failed to kick user:', error);
      toast.error('Не удалось исключить пользователя');
    });
  }, [socket]);

  const lowerParticipantHand = useCallback((participantId: string) => {
    if (!socket) return;
    
    socket.emit('lowerParticipantHand', { participantId }).catch((error) => {
      console.error('Failed to lower participant hand:', error);
      toast.error('Не удалось опустить руку участника');
    });
  }, [socket]);

  const setBitrateLevel = useCallback((level: 'standard' | 'low') => {
    if (!socket) return;
    
    socket.emit('setBitrateLevel', { level }).then((response) => {
      if (response.success) {
        toast.success(level === 'low' ? 'Битрейт понижен' : 'Битрейт восстановлен');
      }
    }).catch((error) => {
      console.error('Failed to set bitrate level:', error);
      toast.error('Не удалось изменить битрейт');
    });
  }, [socket]);

  const endMeeting = useCallback(() => {
    if (!socket) return;
    
    socket.emit('endMeeting', {}).catch((error) => {
      console.error('Failed to end meeting:', error);
      toast.error('Не удалось завершить конференцию');
    });
  }, [socket]);

  const toggleRaisedHand = useCallback(() => {
    if (!socket) return;
    
    const { localParticipant, updateLocalParticipant } = useParticipantsStore.getState();
    if (!localParticipant) return;
    
    const newRaisedHand = !localParticipant.raisedHand;
    updateLocalParticipant({ raisedHand: newRaisedHand });
    
    socket.emit('toggleRaisedHand', { raisedHand: newRaisedHand }).catch((error) => {
      console.error('Failed to toggle raised hand:', error);
      toast.error('Не удалось обновить статус руки');
    });
    
    toast.success(newRaisedHand ? 'Рука поднята' : 'Рука опущена');
  }, [socket]);

  // Быстрая синхронизация участников и producers
  const resyncProducers = useCallback(async () => {
    if (!socket || !mediasoup) {
      toast.error('Нет соединения');
      return;
    }

    try {
      toast.loading('Синхронизация...', { id: 'resync' });
      console.log('[Room] Manual resync...');
      
      const response = await socket.emit('getProducers', {});
      if (!response.success) {
        throw new Error(response.error || 'Failed to get producers');
      }
      
      const { participants, localParticipant } = useParticipantsStore.getState();
      const existingPeerIds = new Set(participants.keys());
      const serverPeerIds = new Set((response.peers || []).map((p: { id: string }) => p.id));
      
      // Удаляем участников, которых нет на сервере (замороженные плитки)
      let peersRemoved = 0;
      for (const peerId of existingPeerIds) {
        // Не удаляем локального участника
        if (localParticipant && peerId === localParticipant.id) continue;
        
        if (!serverPeerIds.has(peerId)) {
          console.log(`[Room] Removing stale peer ${peerId}`);
          removeParticipant(peerId);
          peersRemoved++;
        }
      }
      
      // Добавляем недостающих участников и обновляем isHost у существующих
      let peersAdded = 0;
      let peersUpdated = 0;
      if (response.peers && response.peers.length > 0) {
        console.log(`[Room] Found ${response.peers.length} peers to check`);
        for (const peer of response.peers) {
          if (!existingPeerIds.has(peer.id)) {
            console.log(`[Room] Adding missing peer ${peer.id} (${peer.displayName})`);
            addParticipant({
              id: peer.id,
              userId: peer.userId,
              displayName: peer.displayName,
              isHost: peer.isHost || false,
              audioEnabled: peer.audioEnabled !== false,
              videoEnabled: peer.videoEnabled !== false,
              screenShareEnabled: false,
              raisedHand: false,
              isSpeaking: false,
            });
            peersAdded++;
          } else {
            // Обновляем статусы существующих участников
            const existingParticipant = participants.get(peer.id);
            const nextAudioEnabled = peer.audioEnabled !== false;
            const nextVideoEnabled = peer.videoEnabled !== false;
            const nextIsHost = peer.isHost || false;

            if (
              existingParticipant && (
                existingParticipant.isHost !== nextIsHost ||
                existingParticipant.audioEnabled !== nextAudioEnabled ||
                existingParticipant.videoEnabled !== nextVideoEnabled
              )
            ) {
              console.log(`[Room] Updating participant ${peer.id}: isHost=${nextIsHost}, audio=${nextAudioEnabled}, video=${nextVideoEnabled}`);
              updateParticipant(peer.id, {
                isHost: nextIsHost,
                audioEnabled: nextAudioEnabled,
                videoEnabled: nextVideoEnabled,
              });
              peersUpdated++;
            }
          }
        }
      }
      
      // Синхронизируем producers
      let producersSynced = 0;
      let orphanedClosed = 0;
      
      const existingConsumers = mediasoup.getConsumers();
      const serverProducerIds = new Set((response.producers || []).map((p: any) => p.producerId));
      
      // Закрываем orphaned consumers (consumers для producer'ов которых больше нет на сервере)
      for (const [consumerId, consumer] of existingConsumers) {
        if (!serverProducerIds.has(consumer.producerId)) {
          try {
            mediasoup.closeConsumer(consumerId);
            orphanedClosed++;
            console.log(`[Room] Closed orphaned consumer ${consumerId} (producer ${consumer.producerId} no longer exists)`);
          } catch (err) {
            console.warn(`[Room] Failed to close orphaned consumer ${consumerId}:`, err);
          }
        }
      }
      
      if (orphanedClosed > 0) {
        console.log(`[Room] Closed ${orphanedClosed} orphaned consumers`);
      }
      
      if (response.producers && response.producers.length > 0) {
        console.log(`[Room] Found ${response.producers.length} producers to check`);
        
        // Обновляем список consumers после очистки orphaned
        const currentConsumers = mediasoup.getConsumers();
        const consumedProducerIds = new Set<string>();
        const brokenProducerIds = new Set<string>(); // Includes stale and ended tracks
        const now = Date.now();
        
        currentConsumers.forEach((consumer) => {
          consumedProducerIds.add(consumer.producerId);
        });

        for (const [consumerId, consumer] of currentConsumers) {
          // Check for closed consumers
          if (consumer.closed) {
            console.log(`[Room] Consumer ${consumerId} is closed, marking producer ${consumer.producerId} as broken`);
            brokenProducerIds.add(consumer.producerId);
            continue;
          }
          
          // Check for ended tracks
          if (consumer.track && consumer.track.readyState === 'ended') {
            console.log(`[Room] Consumer ${consumerId} track is ended, marking producer ${consumer.producerId} as broken`);
            brokenProducerIds.add(consumer.producerId);
            continue;
          }
          
          // Check for stale consumers (no packets for 5 seconds)
          try {
            const stats = await consumer.getStats();
            const inbound = Array.from(stats.values()).find((stat: any) => stat.type === 'inbound-rtp');
            const lastPacketTimestamp = inbound?.lastPacketReceivedTimestamp;
            if (typeof lastPacketTimestamp === 'number' && now - lastPacketTimestamp > 5000) {
              brokenProducerIds.add(consumer.producerId);
              console.warn(
                `[Room] Consumer ${consumerId} for producer ${consumer.producerId} appears stale (${Math.round(
                  now - lastPacketTimestamp
                )}ms without packets)`
              );
            }
          } catch (error) {
            console.warn(`[Room] Failed to read stats for consumer ${consumerId}:`, error);
            // If we can't read stats, assume it might be broken
            brokenProducerIds.add(consumer.producerId);
          }
        }
        
        console.log(`[Room] Broken producers: ${brokenProducerIds.size}, Consumed producers: ${consumedProducerIds.size}`);
        
        for (const producer of response.producers) {
          const isConsumed = consumedProducerIds.has(producer.producerId);
          const isBroken = brokenProducerIds.has(producer.producerId);
          
          if (isConsumed && !isBroken) {
            console.log(`[Room] Already consuming producer ${producer.producerId}, skipping`);
            continue;
          }
          
          if (isBroken) {
            console.log(`[Room] Recreating broken consumer for producer ${producer.producerId}`);
            currentConsumers.forEach((consumer, consumerId) => {
              if (consumer.producerId === producer.producerId) {
                try {
                  mediasoup.closeConsumer(consumerId);
                } catch (e) {
                  console.warn(`[Room] Failed to close consumer ${consumerId}:`, e);
                }
              }
            });
          }
          
          try {
            console.log(`[Room] Syncing producer ${producer.producerId} (${producer.kind}) from ${producer.peerId}`);
            await consumeRemoteProducer(
              mediasoup,
              socket,
              producer.peerId,
              producer.producerId,
              producer.kind,
              producer.appData
            );
            producersSynced++;
          } catch (err) {
            console.error(`[Room] Failed to sync producer ${producer.producerId}:`, err);
          }
        }
      }
      
      const hasChanges = peersAdded > 0 || peersRemoved > 0 || producersSynced > 0 || orphanedClosed > 0;
      const message = hasChanges
        ? `+${peersAdded} -${peersRemoved} уч., ${producersSynced} потоков${orphanedClosed > 0 ? `, -${orphanedClosed} consumers` : ''}`
        : 'Всё синхронизировано';
      toast.success(message, { id: 'resync' });
      console.log(`[Room] Resync complete: ${peersAdded} added, ${peersRemoved} removed, ${producersSynced} producers synced, ${orphanedClosed} orphaned consumers closed`);
    } catch (error) {
      console.error('[Room] Failed to resync:', error);
      toast.error('Ошибка синхронизации', { id: 'resync' });
    }
  }, [socket, mediasoup, addParticipant, removeParticipant, updateParticipant, setParticipantStream, setParticipantScreenStream]);

  useEffect(() => {
    resyncProducersRef.current = resyncProducers;
  }, [resyncProducers]);

  useEffect(() => {
    socketRef.current = socket;
  }, [socket]);

  useEffect(() => {
    mediasoupRef.current = mediasoup;
  }, [mediasoup]);

  useEffect(() => {
    localStreamRef.current = localStream;
  }, [localStream]);

  // Cleanup при размонтировании
  useEffect(() => {
    return () => {
      const recorder = recordingMediaRecorderRef.current;
      if (recorder && recorder.state !== 'inactive') {
        recorder.stop();
      } else {
        recordingStreamRef.current?.getTracks().forEach((track) => track.stop());
        recordingScreenStreamRef.current?.getTracks().forEach((track) => track.stop());
        recordingAudioContextRef.current?.close().catch(() => {});
      }

      localStreamRef.current?.getTracks().forEach((track) => track.stop());
      mediasoupRef.current?.close();
      socketRef.current?.disconnect();
    };
  }, []);

  const value = {
    socket,
    mediasoup,
    joinRoom,
    leaveRoom,
    toggleAudio,
    toggleVideo,
    startScreenShare,
    stopScreenShare,
    muteParticipant,
    muteAllParticipants,
    makeAdmin,
    kickUser,
    lowerParticipantHand,
    endMeeting,
    toggleRaisedHand,
    resyncProducers,
    bitrateLevel,
    setBitrateLevel,
    recordingState,
    startLocalRecording,
    stopLocalRecording,
  };

  return <RoomContext.Provider value={value}>{children}</RoomContext.Provider>;
};
